<template>
  <div
    class="block-navigation-quiz"
    :class="{ 'is-on-grid': isOnGrid }"
  >
    <div class="block-navigation-quiz__inner">
      <AtomTextHeading
        v-if="title"
        html-tag="h2"
        :text="title"
        class="block-navigation-quiz__title"
        font-size="h3"
      />

      <AtomTextRichtext
        v-if="text"
        :html="text"
        class="block-navigation-quiz__text"
      />

      <BlockFilterTabs
        v-if="filterTabs?.length > 0"
        :tabs="filterTabs"
        class="block-navigation-quiz__tabs"
        :initial-active-tab="filterTabs[0].name"
        @on-tab-click="handleTabClick"
      />

      <BlockTableRows
        :rows="dummyData ? dummyData : filteredData"
        :no-results-text="noResultsText"
      />
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
    isOnGrid: {
        type: Boolean,
        default: false,
    },

    title: {
        type: String,
        default: '',
    },

    text: {
        type: String,
        default: '',
    },

    filterTabs: {
        type: Array,
        default: () => [],
    },

    columnsSettings: {
        type: Object,
        default: () => {},
    },

    dummyData: {
        type: Array,
        default: null,
    },

    noResultsText: {
        type: String,
        default: null,
    },
});

/*
  Data fetching
*/
const {
    data: fetchedData,
    error,
} = useLazyAsyncData(
    'get-quizzes',
    useGetQuizzes,
    {
        transform: (response) => {
            if (isArrayNotEmpty(response)) {
                const settingsKey = response.completed ? 'completed' : 'incomplete';
                const columnSetting = props.columnsSettings[settingsKey];

                const mappedData = response.map((row) => {
                    const columns = columnSetting.map((column) => {
                        const content = Object.entries(
                            column.content,
                        ).reduce((acc, [key, value]) => {
                            const compiledvalue = compileWithHandlebars(value, row);
                            acc[key] = key === 'progress' ? parseInt(compiledvalue, 10) : compiledvalue;
                            return acc;
                        }, {});

                        return {
                            ...column,
                            content,
                        };
                    });

                    return {
                        completed: row.completed,
                        uuid: row.uuid,
                        columns,
                    };
                });

                return mappedData;
            }

            return [];
        },
    },
);

/*
    Error handling
*/
watch(() => error.value, (e) => {
    if (e) {
        useSentryError(e, {
            useThrow: false,
            response: fetchedData?.value,
        });
    }
});

/*
    Filtering
*/
const currentFilter = ref('all');
const handleTabClick = (event) => {
    currentFilter.value = event;
};

const filteredData = computed(() => {
    if (!fetchedData.value) return [];

    switch (currentFilter.value) {
    case 'all':
        return fetchedData.value;
    case 'complete':
        return fetchedData.value.filter((row) => row.completed);
    case 'incomplete':
        return fetchedData.value.filter((row) => !row.completed);
    default:
        return fetchedData.value;
    }
});
</script>

<style lang="scss" scoped>
.block-navigation-quiz {
    &.is-on-grid {
        @include wrapper-layout();
        @include grid-layout();

        margin-bottom: var(--m-bottom--medium);
    }
}

.block-navigation-quiz__inner {
    @include box-default;

    display: flex;
    flex-direction: column;
    row-gap: 20px;

    @include tablet {
        row-gap: 20px;
    }

    .is-on-grid & {
        @include default-content-columns('wide');
    }
}

.block-navigation-quiz__tabs {
    @include fluid('--parent-padding', 38px, 32px, 60px, false);
    width: calc(100% + var(--parent-padding) * 2);
    margin-left: calc(var(--parent-padding) * -1);

    ::v-deep(.atom-tab) {
        &:first-child {
            margin-left: var(--parent-padding);
        }

        &:last-child {
            margin-right: var(--parent-padding);
        }
    }
}
</style>
